import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import PageShell from "../components/page-shell";
import FilterField from "../components/mobile/filter-field";
import SelectInput from "../components/select-input";
import DataTable, { TableRowData } from "../components/data-table";
import { vendorsTableColumns } from "../lib/table-columns";
import FormDrawer from "../components/form-drawer";
import { vendorFormFields } from "../lib/form-fields";
import SearchInput from "../components/search-input";
import { Edit } from "lucide-react";
import BarGraph from "../components/bar-graph";
import HorizontalBarGraph from "../components/horizontal-bar-graph";
import {
  getVendors,
  getVendorStatistics,
  createVendor,
  updateVendor,
  CreateVendorPayload,
  VendorStatistics,
} from "../lib/api";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { toast } from "sonner";
import { getBackendErrorMessage } from "../lib/utils";

const Vendors: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string>("all");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [formValues, setFormValues] = useState<{
    [key: string]: unknown;
  } | null>(null);

  const queryClient = useQueryClient();

  const vendorsQuery = useQuery({
    queryKey: ["vendors", { page: currentPage, limit: pageSize, search: searchTerm }],
    queryFn: async () => getVendors(currentPage, pageSize, searchTerm),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<VendorStatistics>({
    queryKey: ["vendors", "statistics"],
    queryFn: async () => getVendorStatistics(),
  });

  const vendors = useMemo(() => {
    const data = vendorsQuery.data?.data ?? [];
    const mapped = data.map((v) => ({
      id: v.id,
      name: v.fullName,
      phone_number: v.phoneNumber,
      email: v.email,
      address: v.address,
      country: v.country,
      raw_data: v,
    })) as TableRowData[];
    if (!selectedCountry || selectedCountry === "all") return mapped;
    return mapped.filter((v) => (v as any).raw_data?.country === selectedCountry);
  }, [vendorsQuery.data, selectedCountry]);

  const statistics = statisticsQuery.data ?? null;
  const totalItems = vendorsQuery.data?.meta.total ?? 0;
  const totalPages = vendorsQuery.data?.meta.totalPages ?? 1;
  const initialLoading = vendorsQuery.isLoading || statisticsQuery.isLoading;
  const tableLoading = vendorsQuery.isFetching && !vendorsQuery.isLoading;

  // no-op: queries react to deps

  // Reset pagination on tab or country changes (search handled separately)
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCountry]);

  // Debounce raw search input and apply to `searchTerm` after a short delay
  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  // When searchTerm changes (debounced above), reset pagination to page 1.
  useEffect(() => {
    if (currentPage !== 1) setCurrentPage(1);
  }, [searchTerm]);
  const actions = [
    {
      label: "View details",
      icon: Icons.Preview,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);
        setFormValues({
          name: raw.fullName,
          phone_number: raw.phoneNumber,
          email: raw.email,
          address: raw.address,
          country: raw.country,
        });
      },
    },
    {
      label: "Edit vendor",
      icon: Edit,
      onClick: (row: TableRowData) => {
        const raw = (row as any).raw_data;
        setEditingId(raw.id);
        setFormValues({
          name: raw.fullName,
          phone_number: raw.phoneNumber,
          email: raw.email,
          address: raw.address,
          country: raw.country,
        });
      },
    },
  ];

  const handleFormSubmit = async () => {
    if (!formValues) return;

    setIsSubmitting(true);

    const payload: CreateVendorPayload = {
      fullName: formValues.name as string,
      phoneNumber: formValues.phone_number as string,
      email: formValues.email as string,
      address: formValues.address as string,
      country: formValues.country as string,
    };

    try {
      if (editingId) {
        await updateVendor(editingId, payload);
      } else {
        await createVendor(payload);
      }
      setFormValues(null);
      setEditingId(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["vendors"] }),
        queryClient.invalidateQueries({ queryKey: ["vendors", "statistics"] }),
      ]);
    } catch (error) {
      toast.error(getBackendErrorMessage(error, "Failed to save vendor"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const countryMetrics =
    (statistics?.byCountry.map((c) => ({
      label: c.country,
      count: c.count,
      percentage: Math.round(((c.count) / (statistics?.totalVendors || 1)) * 100),
      avgDays: 0,
    })) || []).sort((a, b) => b.count - a.count);

  const vendorData =
    (statistics?.topVendors ?? []).map((v) => ({
      category: v.vendorName,
      gap: 0.2,
      values: [
        {
          label: "Movements",
          value: v.totalMovements,
          fillColor: "#bce3ff",
        },
        {
          label: "Items",
          value: v.totalItems,
          fillColor: "#e9d5ff",
        },
        {
          label: "Quantity",
          value: v.totalQuantity,
          fillColor: "#ffd6a8",
        },
      ],
    }));

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ["vendors"] }),
      queryClient.invalidateQueries({ queryKey: ["vendors", "statistics"] }),
    ]);

  const openNewVendor = () => setFormValues({});

  const countrySelect = (
    <SelectInput
      value={selectedCountry}
      options={[
        { label: "All", value: "all" },
        ...(statistics?.byCountry.map((c) => ({
          label: c.country,
          value: c.country,
        })) || []),
      ]}
      placeholder={"All Countries"}
      onChange={(value) => setSelectedCountry(value)}
    />
  );

  return (
    <>
      <FormDrawer
        title={editingId ? "Edit Vendor" : "Add New Vendor"}
        buttonText={editingId ? "Update Vendor" : "Add Vendor"}
        formValues={formValues}
        setFormValues={setFormValues}
        fields={vendorFormFields}
        handleSubmit={handleFormSubmit}
        isSubmitting={isSubmitting}
        onClose={() => {
          setFormValues(null);
          setEditingId(null);
        }}
      />
      <PageShell
        title="Vendors"
        description="Manage vendors and suppliers for your inventory purchases."
        loading={initialLoading}
        toolbar={
          <>
            {countrySelect}
            <RefreshToolbarButton
              isRefreshing={vendorsQuery.isFetching || statisticsQuery.isFetching}
              onRefresh={refresh}
            />
            <Button variant="default" className="h-9" onClick={openNewVendor}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              New Vendor
            </Button>
          </>
        }
        stats={[
          {
            label: "Total Vendors",
            value: statistics?.totalVendors.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-primary-600" />,
          },
          {
            label: "Total Movements",
            value: statistics?.totals.movements.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-green-600" />,
          },
          {
            label: "Total Items",
            value: statistics?.totals.items.toString() || "0",
            icon: <Icons.Building className="w-5 h-5 text-blue-600" />,
          },
        ]}
        insights={
          <div className="grid grid-cols-1 lg:grid-cols-[1fr,400px] gap-4 mt-6">
            <HorizontalBarGraph
              graphTitle="Vendor Activity Summary"
              data={vendorData.slice(0, 3)}
            />
            <BarGraph graphTitle="Vendors by Country" metrics={countryMetrics.slice(0, 3)} />
          </div>
        }
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "New Vendor", icon: Icons.Plus, onClick: openNewVendor },
          filters: <FilterField label="Country">{countrySelect}</FilterField>,
          activeFilterCount: selectedCountry !== "all" ? 1 : 0,
          onResetFilters: () => setSelectedCountry("all"),
        }}
      >
        <SearchInput
          searchTerm={searchInput}
          onSearchChange={(value) => setSearchInput(value)}
          placeholder="Search vendors..."
        />
        <DataTable
          columns={vendorsTableColumns}
          data={vendors}
          actions={actions}
          loading={tableLoading}
          pagination={{
            page: currentPage,
            pageSize,
            totalItems,
            totalPages,
            onPageChange: (page) => {
              setCurrentPage(page);
            },
            onPageSizeChange: (size) => {
              setPageSize(size);
            },
          }}
        />
      </PageShell>
    </>
  );
};

export default Vendors;
